import { FC, useEffect, useMemo, useRef, useState } from "react";
import "./SectionView.scss";
import MainNavbar from "../../../components/MainNavbar/MainNavbar";
import HeaderPage from "../../../components/HeaderPage/HeaderPage";
import { Col, Container, Row } from "react-bootstrap";
import { Link, useParams, useSearchParams } from "react-router-dom";
import BreadcrumbPage from "../../../components/BreadcrumbPage/BreadcrumbPage";
import { useSections } from "../../../providers/Articles/Sections/SectionsProvider";
import ListArticlesView from "../../../components/Articles/ListArticlesView/ListArticlesView";
import ScrollTopButton from "../../../components/ScrollTopButton/ScrollTopButton";
import { ArticlesService } from "../../../services/Articles/ArticlesService";

interface SectionViewProps {}

const articlesService = new ArticlesService();

// Agrupamento visual de "GOVERNO DE SÃO PAULO - X" sob "GOVERNO DE SÃO
// PAULO", sem hierarquia real no banco. Restrito só a São Paulo por ora.
const SP_BASE_NAME = "GOVERNO DE SÃO PAULO";

const SectionView: FC<SectionViewProps> = () => {
  const [category, setCategory] = useState<any>(null);
  const [ownStructure, setOwnStructure] = useState<any[]>([]);
  const [categorySections, setCategorySections] = useState<any[]>([]);
  const ownStructureRequestRef = useRef(0);
  const categorySectionsRequestRef = useRef(0);
  const loadedCategoryIdRef = useRef<number | null>(null);

  const { sections, setParams } = useSections();

  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const showOwnContent = searchParams.get("geral") === "1";

  const currentSection = sections.find((s) => Number(s.id) === Number(id));
  const categoryId = currentSection?.category?.id;
  // ownStructure é buscado especificamente para este id e chega mais rápido
  // e mais confiável do que "sections" (useSections), que às vezes ainda
  // reflete a seção anterior por um instante ao trocar de página.
  const currentName = ownStructure[0]?.section_name ?? currentSection?.name;
  const currentDescription =
    ownStructure[0]?.section_description ?? currentSection?.description;

  // Conteúdo próprio da seção, buscado à parte pra não depender do estado
  // global de artigos (evita mostrar conteúdo de outra seção por engano).
  useEffect(() => {
    if (!id) return;

    setCategory(null);
    setOwnStructure([]);

    setParams({ filter: { id: Number(id) }, withPaginate: false });

    const requestId = ++ownStructureRequestRef.current;

    (async () => {
      const [_Response] = await articlesService.listByStructure({
        filter: { section_id: Number(id) },
      });

      if (requestId !== ownStructureRequestRef.current) return;

      setOwnStructure(_Response?.data?.items || []);
    })();
  }, [id]);

  useEffect(() => {
    if (currentSection) {
      setCategory(currentSection.category);
    }
  }, [currentSection]);

  // Lista completa de seções da categoria, buscada uma vez só (não a cada
  // troca de seção) pra descobrir se a atual é "GOVERNO DE SÃO PAULO" ou
  // uma de suas variações.
  useEffect(() => {
    if (!categoryId) return;
    if (loadedCategoryIdRef.current === categoryId) return;

    const requestId = ++categorySectionsRequestRef.current;

    (async () => {
      const [_Response] = await articlesService.listByStructure({
        filter: { category_id: categoryId },
      });

      if (requestId !== categorySectionsRequestRef.current) return;

      loadedCategoryIdRef.current = categoryId;
      setCategorySections(_Response?.data?.items?.[0]?.sections || []);
    })();
  }, [categoryId]);

  const relatedSections = useMemo(() => {
    if (currentName !== SP_BASE_NAME) return [];
    return categorySections.filter(
      (s: any) =>
        s.section_name?.startsWith(`${SP_BASE_NAME} - `) &&
        Number(s.section_id) !== Number(id),
    );
  }, [categorySections, currentName, id]);

  const parentSection = useMemo(() => {
    if (!currentName?.startsWith(`${SP_BASE_NAME} - `)) return null;
    const parent = categorySections.find(
      (s: any) => s.section_name === SP_BASE_NAME,
    );
    return parent ? { id: parent.section_id, name: parent.section_name } : null;
  }, [categorySections, currentName]);

  const hasGroup = relatedSections.length > 0;

  return (
    <>
      <MainNavbar />
      <div className="SectionView" data-testid="SectionView">
        <HeaderPage
          title={currentName ?? ""}
          subtitle={currentDescription ?? ""}
        />

        <div className="breadcrumb-wrapper">
          <Container>
            <Row className="justify-content-center">
              <Col md={10}>
                <BreadcrumbPage
                  category={category}
                  parentSection={parentSection ?? undefined}
                  section={ownStructure[0] ?? currentSection}
                />
              </Col>
            </Row>
          </Container>
        </div>
        <div className="content-wrapper">
          <Container>
            <Row className="justify-content-center py-sm-5 py-3">
              <Col md={10}>
                {hasGroup && !showOwnContent ? (
                  <div className="ListArticlesView">
                    <div className="mb-5">
                      <div className="section-header">
                        <h3>
                          <Link to={`/articles/sections/view/${id}?geral=1`}>
                            {currentName} - GERAL
                          </Link>
                        </h3>
                      </div>
                    </div>

                    {relatedSections.map((variant: any) => (
                      <div key={variant.section_id} className="mb-5">
                        <div className="section-header">
                          <h3>
                            <Link
                              to={`/articles/sections/view/${variant.section_id}`}
                            >
                              {variant.section_name}
                            </Link>
                          </h3>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <ListArticlesView
                    articlesStructure={ownStructure}
                    viewMode="section"
                  />
                )}
              </Col>
            </Row>
          </Container>
        </div>
        <ScrollTopButton />
      </div>
    </>
  );
};

export default SectionView;
